import { useState } from "react";

import { AGENTIC_LOGIC_SURFACE_LABEL } from "@intelligems/ig-types/dtos/agentic-logic/surface";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { Box, Skeleton, Stack, useTheme } from "@mui/material";

import { RULE_GRAPH_RESULT_WIDTH } from "./layout";
import type {
  RuleGraphCartInput as RuleGraphCartInputData,
  RuleGraphCartInputLine,
} from "./types";

const MAX_VISIBLE_LINES = 3;

const LineThumbnail = ({ line }: { line: RuleGraphCartInputLine }) => {
  const theme = useTheme();
  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null);
  const hasImage = Boolean(line.imageUrl) && line.imageUrl !== failedImageUrl;

  if (!hasImage || !line.imageUrl) {
    // The same still, un-alarmed square the shopper-sees panel uses: a
    // missing picture is cosmetic, and nothing is loading.
    return (
      <Skeleton
        animation={false}
        aria-label={`No image for ${line.title}`}
        data-testid={`cart-input-image-placeholder-${line.id}`}
        height="28px"
        sx={{
          backgroundColor: theme.igBackgroundColors.baseLightLow,
          borderRadius: "4px",
          flexShrink: 0,
        }}
        variant="rounded"
        width="28px"
      />
    );
  }

  return (
    <Box
      alt={line.title}
      component="img"
      onError={() => setFailedImageUrl(line.imageUrl ?? null)}
      src={line.imageUrl}
      sx={{
        borderRadius: "4px",
        flexShrink: 0,
        height: "28px",
        objectFit: "cover",
        width: "28px",
      }}
    />
  );
};

const Caption = ({ text, testId }: { text: string; testId?: string }) => {
  const theme = useTheme();
  return (
    <IgTypography
      component="p"
      data-testid={testId}
      noWrap
      sx={{
        color: theme.palette.text.tertiary,
        fontSize: "10px",
        lineHeight: 1.5,
        margin: 0,
      }}
    >
      {text}
    </IgTypography>
  );
};

/**
 * "Cart" — the panel at the head of the rows while a run is on the canvas:
 * what the shopper had in the cart, the subtotal, and whichever request
 * context the rule conditions on. The sibling of {@link RuleGraphShopperOutput}
 * on the other side of the rows, in the same footprint and voice.
 */
export const RuleGraphCartInput = ({
  cartInput,
}: {
  cartInput: RuleGraphCartInputData;
}) => {
  const theme = useTheme();
  const visibleLines = cartInput.lines.slice(0, MAX_VISIBLE_LINES);
  const hiddenLineCount = Math.max(
    cartInput.lines.length - MAX_VISIBLE_LINES,
    0
  );
  const contextRows = [
    cartInput.shipping === undefined
      ? null
      : { key: "shipping", text: cartInput.shipping },
    cartInput.delivery === undefined
      ? null
      : { key: "delivery", text: cartInput.delivery },
    cartInput.customer === undefined
      ? null
      : { key: "customer", text: cartInput.customer },
  ].filter((row) => row !== null);

  return (
    <Stack
      data-testid="cart-input-panel"
      sx={{
        backgroundColor: theme.igBackgroundColors.baseLightHigh,
        border: `1px solid ${theme.igBorders.core.default}`,
        borderRadius: "8px",
        boxShadow: theme.igShadows.low,
        boxSizing: "border-box",
        // Sits in a flex column sized to the card row it centers on and is
        // taller than that row; see RuleGraphShopperOutput.
        flexShrink: 0,
        gap: "10px",
        overflow: "hidden",
        padding: "12px 14px",
        width: `${RULE_GRAPH_RESULT_WIDTH}px`,
      }}
    >
      <Stack
        alignItems="baseline"
        direction="row"
        justifyContent="space-between"
      >
        <IgTypography
          component="p"
          sx={{
            color: theme.palette.text.placeholder,
            fontSize: "10px",
            fontWeight: 600,
            lineHeight: 1.5,
            margin: 0,
          }}
        >
          CART
        </IgTypography>
        <Caption
          testId="cart-input-surface"
          text={AGENTIC_LOGIC_SURFACE_LABEL[cartInput.surface]}
        />
      </Stack>
      {visibleLines.map((line) => (
        <Stack
          alignItems="center"
          data-testid="cart-input-line"
          direction="row"
          gap="10px"
          key={line.id}
          minWidth={0}
        >
          <LineThumbnail line={line} />
          <Stack gap="2px" minWidth={0} overflow="hidden">
            <IgTypography
              component="p"
              noWrap
              sx={{
                color: theme.palette.text.primary,
                fontSize: "10px",
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              {line.title}
            </IgTypography>
            {line.variantTitle ? (
              <IgTypography
                component="p"
                noWrap
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "10px",
                  lineHeight: 1.5,
                  margin: 0,
                }}
              >
                {line.variantTitle}
              </IgTypography>
            ) : null}
            <IgTypography
              component="p"
              sx={{
                color: theme.palette.text.tertiary,
                fontSize: "10px",
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              {`${line.quantity} × ${line.unitPrice}`}
            </IgTypography>
          </Stack>
        </Stack>
      ))}
      {hiddenLineCount > 0 ? (
        <Caption text={`+${hiddenLineCount} more`} />
      ) : null}
      <Box
        data-testid="cart-input-divider"
        sx={{
          borderTop: `1px solid ${theme.igBorders.core.default}`,
          height: 0,
          width: "100%",
        }}
      />
      <Stack gap="2px">
        <Stack
          alignItems="baseline"
          data-testid="cart-input-subtotal"
          direction="row"
          justifyContent="space-between"
        >
          <IgTypography
            component="p"
            sx={{
              color: theme.palette.text.tertiary,
              fontSize: "10px",
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            {`Subtotal · ${cartInput.itemCount} item${cartInput.itemCount === 1 ? "" : "s"}`}
          </IgTypography>
          <IgTypography
            component="p"
            sx={{
              color: theme.palette.text.primary,
              fontSize: "11px",
              fontWeight: 600,
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            {cartInput.subtotal}
          </IgTypography>
        </Stack>
        {contextRows.map((row) => (
          <Caption
            key={row.key}
            testId={`cart-input-${row.key}`}
            text={row.text}
          />
        ))}
      </Stack>
    </Stack>
  );
};
